import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  warehouseOrders,
  type WarehouseOrderListItem,
  type WarehouseOrderDirection,
  type WarehouseOrderState,
} from '@/api/warehouseOrders';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

type DirectionFilter = 'ALL' | WarehouseOrderDirection;
type StateFilter = 'ALL' | WarehouseOrderState;

const DIRECTION_LABEL: Record<WarehouseOrderDirection, string> = {
  IN: 'Приходний',
  OUT: 'Видатковий',
};

const STATE_LABEL: Record<WarehouseOrderState, string> = {
  DRAFT: 'Чернетка',
  COMMITTEE_VERIFIED: 'Перевірено комітетом',
  EXECUTED: 'Виконаний',
  CANCELLED: 'Скасований',
};

export default function WarehouseOrdersPage() {
  const navigate = useNavigate();
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('ALL');
  const [stateFilter, setStateFilter] = useState<StateFilter>('ALL');

  const list = useQuery({
    queryKey: ['warehouse-orders', { directionFilter, stateFilter }],
    queryFn: () =>
      warehouseOrders.list({
        limit: 200,
        ...(directionFilter !== 'ALL' ? { direction: directionFilter } : {}),
        ...(stateFilter !== 'ALL' ? { state: stateFilter } : {}),
      }),
  });

  const cols = useMemo<ColumnDef<WarehouseOrderListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: '№',
        width: 150,
        importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'direction',
        header: 'Напрямок',
        width: 130,
        sortValue: (r) => DIRECTION_LABEL[r.direction] ?? r.direction,
        cell: (r) => DIRECTION_LABEL[r.direction] ?? r.direction,
      },
      {
        key: 'state',
        header: 'Стан',
        width: 200,
        sortValue: (r) => STATE_LABEL[r.state] ?? r.state,
        cell: (r) => STATE_LABEL[r.state] ?? r.state,
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Складські ордери</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem' }}>Напрямок:</label>
          <select
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value as DirectionFilter)}
            data-testid="wo-direction-filter"
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="ALL">Усі</option>
            <option value="IN">Приходні</option>
            <option value="OUT">Видаткові</option>
          </select>
          <label style={{ fontSize: '0.875rem' }}>Стан:</label>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as StateFilter)}
            data-testid="wo-state-filter"
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="ALL">Усі</option>
            <option value="DRAFT">Чернетки</option>
            <option value="COMMITTEE_VERIFIED">Перевірені комітетом</option>
            <option value="EXECUTED">Виконані</option>
            <option value="CANCELLED">Скасовані</option>
          </select>
        </div>
        <button
          className="primary"
          onClick={() => navigate('/warehouse/orders/new')}
          style={{ marginLeft: 'auto' }}
        >
          + Новий ордер
        </button>
      </div>
      <DataTable<WarehouseOrderListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/warehouse/orders/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
